import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  BasketValidation,
  DispensingPrescription,
  DispensingPrescriptionSummary,
  OpticalJobDetail,
  OpticalLabSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { stableStringify } from '@/services/operationKey';
import { validateBasket, type ValidateBasketPayload } from '@/modules/sales/api/sales.api';

/** Outside the sales keys on purpose: a completed sale invalidates those, and must not send this screen's checks again as it leaves. */
export const opticalOrderKeys = {
  all: ['optical-builder'] as const,
  dispensing: (customerId: string, includeUnusable: boolean) =>
    [...opticalOrderKeys.all, 'dispensing', customerId, includeUnusable] as const,
  dispensingOne: (customerId: string, prescriptionId: string) =>
    [...opticalOrderKeys.all, 'dispensing-one', customerId, prescriptionId] as const,
  labs: () => [...opticalOrderKeys.all, 'labs'] as const,
  orderJobs: (orderId: string) => [...opticalOrderKeys.all, 'order-jobs', orderId] as const,
  validation: (fingerprint: string) =>
    [...opticalOrderKeys.all, 'validation', fingerprint] as const,
  candidates: (fingerprint: string) =>
    [...opticalOrderKeys.all, 'candidates', fingerprint] as const,
};

/** The customer's issued prescriptions a pair can be made to, newest first; the unusable ones only when asked, with their reason and nothing else. */
export function useDispensingPrescriptionsQuery(
  customerId: string,
  includeUnusable = false,
  enabled = true,
) {
  return useQuery({
    queryKey: opticalOrderKeys.dispensing(customerId, includeUnusable),
    queryFn: () =>
      api.get<DispensingPrescriptionSummary[]>(
        `/customers/${customerId}/dispensing-prescriptions`,
        {
          params: includeUnusable ? { includeUnusable: true } : {},
        },
      ),
    enabled: enabled && Boolean(customerId),
    staleTime: 0,
  });
}

/** One prescription's final dispensing values, for a read-only preview. Held in memory only, and dropped as soon as nothing shows it. */
export function useDispensingPrescriptionQuery(customerId: string, prescriptionId: string | null) {
  return useQuery({
    queryKey: opticalOrderKeys.dispensingOne(customerId, prescriptionId ?? ''),
    queryFn: () =>
      api.get<DispensingPrescription>(
        `/customers/${customerId}/dispensing-prescriptions/${prescriptionId ?? ''}`,
      ),
    enabled: Boolean(customerId && prescriptionId),
    gcTime: 0,
  });
}

export function useOpticalLabsQuery() {
  return useQuery({
    queryKey: opticalOrderKeys.labs(),
    queryFn: () => api.get<OpticalLabSummary[]>('/optical/labs'),
    staleTime: 5 * 60_000,
  });
}

export function useOrderOpticalJobsQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: opticalOrderKeys.orderJobs(orderId ?? ''),
    queryFn: () => api.get<OpticalJobDetail[]>(`/sales/orders/${orderId ?? ''}/optical-jobs`),
    enabled: Boolean(orderId),
  });
}

/**
 * The server's check of the whole pairs: prices, stock as the checkout takes it, and the refusal the checkout would give.
 *
 * A read the server answers fresh every time; its answer is dropped as soon as nothing shows it, so no prescription value sits in a cache.
 */
export function useBuilderValidationQuery(payload: ValidateBasketPayload | null, enabled = true) {
  const fingerprint = payload ? stableStringify(payload) : '';
  // Stock is the branch's, so the answer is too.
  const branchId = useAuthStore((state) => state.activeBranchId);
  return useQuery<BasketValidation>({
    queryKey: [...opticalOrderKeys.validation(fingerprint), branchId],
    queryFn: () => validateBasket(payload as ValidateBasketPayload),
    enabled: enabled && payload !== null,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** Current price and sellable stock of variants being chosen between, from the same check the checkout trusts, at the branch in use: none is asked without one. No pairs, so nothing about a prescription travels. */
export function useCandidateCheckQuery(variantIds: readonly string[]) {
  const ids = [...new Set(variantIds)].sort();
  const branchId = useAuthStore((state) => state.activeBranchId);
  return useQuery<BasketValidation>({
    queryKey: [...opticalOrderKeys.candidates(ids.join(',')), branchId],
    queryFn: () => validateBasket({ lines: ids.map((variantId) => ({ variantId, quantity: 1 })) }),
    enabled: ids.length > 0 && Boolean(branchId),
    staleTime: 15_000,
  });
}
